import type { Lang } from './types'

export interface Translations {
  title: string
  subtitle: string
  widgetSize: string
  theme: string
  lightMode: string
  darkMode: string
  profiles: string
  contentAdjustments: string
  colorAdjustments: string
  resetAll: string
  close: string
  language: string
  // Profiles
  seizureSafe: string
  visionImpaired: string
  adhdFriendly: string
  cognitiveDisability: string
  keyboardNavigation: string
  screenReader: string
  colorBlind: string
  dyslexia: string
  // Content
  contentScale: string
  readableFont: string
  highlightTitles: string
  fontSize: string
  textMagnifier: string
  readingLens: string
  highlightLinks: string
  lineHeight: string
  letterSpacing: string
  textAlign: string
  // Color
  darkContrast: string
  lightContrast: string
  highContrast: string
  monochrome: string
  invertColors: string
  highSaturation: string
  lowSaturation: string
  adjustTextColors: string
  adjustTitleColors: string
  adjustBackgroundColors: string
  cancel: string
  // Orientation
  orientationAdjustments: string
  readingGuide: string
  stopAnimations: string
  readingMask: string
  highlightHover: string
  bigBlackCursor: string
  bigWhiteCursor: string
  // Stepper
  default: string
  decrease: string
  increase: string
  // Align
  alignLeft: string
  alignCenter: string
  alignRight: string
  // WCAG
  pageAnalysis: string
  analyzeNow: string
  analyzing: string
  score: string
  issuesFound: string
  noIssues: string
  wcagPass: string
  wcagFail: string
  wcagWarn: string
  // Statement
  statement: string
  statementTitle: string
  statementDesc: string
  complianceStatus: string
  statementP1: string
  statementP2: string
  statementP3: string
  closeStatement: string
}

const en: Translations = {
  title: 'Tech unicorn',
  subtitle: 'Accessibility Settings',
  widgetSize: 'Widget Size',
  theme: 'Menu Theme',
  lightMode: 'Light Mode',
  darkMode: 'Dark Mode',
  profiles: 'Accessibility Profiles',
  contentAdjustments: 'Content Adjustments',
  colorAdjustments: 'Color Adjustments',
  resetAll: 'Reset all settings',
  close: 'Close accessibility menu',
  language: 'Language',
  seizureSafe: 'Seizure Safe',
  visionImpaired: 'Vision Impaired',
  adhdFriendly: 'ADHD Friendly',
  cognitiveDisability: 'Cognitive Disability',
  keyboardNavigation: 'Keyboard Navigation',
  screenReader: 'Screen Reader',
  colorBlind: 'Color Blind',
  dyslexia: 'Dyslexia',
  contentScale: 'Content Scale',
  readableFont: 'Readable Font',
  highlightTitles: 'Highlight Titles',
  fontSize: 'Font Size',
  textMagnifier: 'Text Magnifier',
  readingLens: 'Reading Lens',
  highlightLinks: 'Highlight Links',
  lineHeight: 'Line Height',
  letterSpacing: 'Letter Spacing',
  textAlign: 'Text Align',
  darkContrast: 'Dark Contrast',
  lightContrast: 'Light Contrast',
  highContrast: 'High Contrast',
  monochrome: 'Monochrome',
  invertColors: 'Invert Colors',
  highSaturation: 'High Saturation',
  lowSaturation: 'Low Saturation',
  adjustTextColors: 'Adjust Text Colors',
  adjustTitleColors: 'Adjust Title Colors',
  adjustBackgroundColors: 'Adjust Background Colors',
  cancel: 'Cancel',
  orientationAdjustments: 'Orientation Adjustments',
  readingGuide: 'Reading Guide',
  stopAnimations: 'Stop Animations',
  readingMask: 'Reading Mask',
  highlightHover: 'Highlight Hover',
  bigBlackCursor: 'Big Black Cursor',
  bigWhiteCursor: 'Big White Cursor',
  default: 'Default',
  decrease: 'Decrease',
  increase: 'Increase',
  alignLeft: 'Align left',
  alignCenter: 'Align center',
  alignRight: 'Align right',
  pageAnalysis: 'Page Analysis',
  analyzeNow: 'Analyze Page',
  analyzing: 'Analyzing…',
  score: 'Score',
  issuesFound: 'issues found',
  noIssues: 'No issues found',
  wcagPass: 'Pass',
  wcagFail: 'Fail',
  wcagWarn: 'Warning',
  statement: 'Statement',
  statementTitle: 'Accessibility Statement',
  statementDesc: 'WCAG 2.2 AA Compliance & Policies',
  complianceStatus: 'Compliance status',
  statementP1: 'We firmly believe that the internet should be available and accessible to anyone and are committed to providing a website that is accessible to the broadest possible audience, regardless of ability.',
  statementP2: 'To fulfill this, we aim to adhere as strictly as possible to the World Wide Web Consortium’s (W3C) Web Content Accessibility Guidelines 2.2 (WCAG 2.2) at the AA level. These guidelines explain how to make web content accessible to people with a wide array of disabilities. Complying with those guidelines helps us ensure that the website is accessible to blind people, people with motor impairments, visual impairment, cognitive disabilities, and more.',
  statementP3: 'This website utilizes an accessibility interface that allows persons with specific disabilities to adjust the website’s UI (user interface) and design it to their personal needs.',
  closeStatement: 'Close statement',
}

const es: Translations = {
  title: 'Tech unicorn',
  subtitle: 'Configuración de Accesibilidad',
  widgetSize: 'Tamaño del Widget',
  theme: 'Tema del Menú',
  lightMode: 'Modo Claro',
  darkMode: 'Modo Oscuro',
  profiles: 'Perfiles de Accesibilidad',
  contentAdjustments: 'Ajustes de Contenido',
  colorAdjustments: 'Ajustes de Color',
  resetAll: 'Restablecer configuración',
  close: 'Cerrar menú de accesibilidad',
  language: 'Idioma',
  seizureSafe: 'Seguro para Convulsiones',
  visionImpaired: 'Visión Reducida',
  adhdFriendly: 'TDAH Amigable',
  cognitiveDisability: 'Discapacidad Cognitiva',
  keyboardNavigation: 'Navegación por Teclado',
  screenReader: 'Lector de Pantalla',
  colorBlind: 'Daltonismo',
  dyslexia: 'Dislexia',
  contentScale: 'Escala de Contenido',
  readableFont: 'Fuente Legible',
  highlightTitles: 'Resaltar Títulos',
  fontSize: 'Tamaño de Fuente',
  textMagnifier: 'Lupa de Texto',
  readingLens: 'Lente de Lectura',
  highlightLinks: 'Resaltar Enlaces',
  lineHeight: 'Altura de Línea',
  letterSpacing: 'Espaciado de Letras',
  textAlign: 'Alineación de Texto',
  darkContrast: 'Contraste Oscuro',
  lightContrast: 'Contraste Claro',
  highContrast: 'Alto Contraste',
  monochrome: 'Monocromático',
  invertColors: 'Invertir Colores',
  highSaturation: 'Saturación Alta',
  lowSaturation: 'Saturación Baja',
  adjustTextColors: 'Ajustar Colores de Texto',
  adjustTitleColors: 'Ajustar Colores de Título',
  adjustBackgroundColors: 'Ajustar Colores de Fondo',
  cancel: 'Cancelar',
  orientationAdjustments: 'Ajustes de Orientación',
  readingGuide: 'Guía de Lectura',
  stopAnimations: 'Detener Animaciones',
  readingMask: 'Máscara de Lectura',
  highlightHover: 'Resaltar al Pasar',
  bigBlackCursor: 'Cursor Negro Grande',
  bigWhiteCursor: 'Cursor Blanco Grande',
  default: 'Predeterminado',
  decrease: 'Disminuir',
  increase: 'Aumentar',
  alignLeft: 'Alinear izquierda',
  alignCenter: 'Centrar',
  alignRight: 'Alinear derecha',
  pageAnalysis: 'Análisis de Página',
  analyzeNow: 'Analizar Página',
  analyzing: 'Analizando…',
  score: 'Puntuación',
  issuesFound: 'problemas encontrados',
  noIssues: 'No se encontraron problemas',
  wcagPass: 'Correcto',
  wcagFail: 'Fallo',
  wcagWarn: 'Advertencia',
  statement: 'Declaración',
  statementTitle: 'Declaración de Accesibilidad',
  statementDesc: 'Cumplimiento de WCAG 2.2 AA y políticas',
  complianceStatus: 'Estado de conformidad',
  statementP1: 'Creemos firmemente que Internet debe estar disponible y accesible para cualquier persona y nos comprometemos a proporcionar un sitio web accesible para el público más amplio posible, independientemente de sus capacidades.',
  statementP2: 'Para cumplir esto, intentamos adherirnos lo más estrictamente posible a las Pautas de Accesibilidad para el Contenido Web 2.2 (WCAG 2.2) del Consorcio World Wide Web (W3C) en el nivel AA. Estas pautas explican cómo hacer que el contenido web sea accesible para personas con diversas discapacidades.',
  statementP3: 'Este sitio web utiliza una interfaz de accesibilidad que permite a las personas con discapacidades específicas ajustar la interfaz de usuario del sitio web y adaptarla a sus necesidades personales.',
  closeStatement: 'Cerrar declaración',
}

const fr: Translations = {
  title: 'Tech unicorn',
  subtitle: "Paramètres d'Accessibilité",
  widgetSize: 'Taille du Widget',
  theme: 'Thème du Menu',
  lightMode: 'Mode Clair',
  darkMode: 'Mode Sombre',
  profiles: "Profils d'Accessibilité",
  contentAdjustments: 'Ajustements du Contenu',
  colorAdjustments: 'Ajustements des Couleurs',
  resetAll: 'Réinitialiser les paramètres',
  close: "Fermer le menu d'accessibilité",
  language: 'Langue',
  seizureSafe: 'Sûr pour Épilepsie',
  visionImpaired: 'Déficience Visuelle',
  adhdFriendly: 'TDAH Adapté',
  cognitiveDisability: 'Handicap Cognitif',
  keyboardNavigation: 'Navigation Clavier',
  screenReader: "Lecteur d'Écran",
  colorBlind: 'Daltonisme',
  dyslexia: 'Dyslexie',
  contentScale: 'Échelle du Contenu',
  readableFont: 'Police Lisible',
  highlightTitles: 'Surligner les Titres',
  fontSize: 'Taille de Police',
  textMagnifier: 'Loupe de Texte',
  readingLens: 'Loupe de Lecture',
  highlightLinks: 'Surligner les Liens',
  lineHeight: 'Hauteur de Ligne',
  letterSpacing: 'Espacement des Lettres',
  textAlign: 'Alignement du Texte',
  darkContrast: 'Contraste Sombre',
  lightContrast: 'Contraste Clair',
  highContrast: 'Contraste Élevé',
  monochrome: 'Monochrome',
  invertColors: 'Invertir Couleurs',
  highSaturation: 'Saturation Élevée',
  lowSaturation: 'Basse Saturation',
  adjustTextColors: 'Ajuster Couleurs Texte',
  adjustTitleColors: 'Ajuster Couleurs Titre',
  adjustBackgroundColors: 'Ajuster Couleurs Fond',
  cancel: 'Annuler',
  orientationAdjustments: "Ajustements d'Orientation",
  readingGuide: 'Guide de Lecture',
  stopAnimations: 'Arrêter les Animations',
  readingMask: 'Masque de Lecture',
  highlightHover: 'Surligner au Survol',
  bigBlackCursor: 'Grand Curseur Noir',
  bigWhiteCursor: 'Grand Curseur Blanc',
  default: 'Défaut',
  decrease: 'Diminuer',
  increase: 'Augmenter',
  alignLeft: 'Aligner à gauche',
  alignCenter: 'Centrer',
  alignRight: 'Aligner à droite',
  pageAnalysis: 'Analyse de Page',
  analyzeNow: 'Analyser la Page',
  analyzing: 'Analyse en cours…',
  score: 'Score',
  issuesFound: 'problèmes trouvés',
  noIssues: 'Aucun problème trouvé',
  wcagPass: 'Réussi',
  wcagFail: 'Échec',
  wcagWarn: 'Avertissement',
  statement: 'Déclaration',
  statementTitle: "Déclaration d'Accessibilité",
  statementDesc: 'Conformité WCAG 2.2 AA et politiques',
  complianceStatus: 'État de conformité',
  statementP1: "Nous croyons fermement qu'Internet doit être disponible et accessible à tous et nous nous engageons à fournir un site Web accessible au public le plus large possible, quelles que soient leurs capacités.",
  statementP2: "Pour ce faire, nous visons à respecter le plus fidèlement possible les règles pour l'accessibilité des contenus Web 2.2 (WCAG 2.2) du World Wide Web Consortium (W3C) au niveau AA. Ces directives expliquent comment rendre le contenu Web accessible aux personnes souffrant d'un large éventail de handicaps.",
  statementP3: "Ce site Web utilise une interface d'accessibilité qui permet aux personnes souffrant de handicaps spécifiques d'ajuster l'interface utilisateur du site Web et de la concevoir selon leurs besoins personnels.",
  closeStatement: 'Fermer la déclaration',
}

const de: Translations = {
  title: 'Tech unicorn',
  subtitle: 'Barrierefreiheitseinstellungen',
  widgetSize: 'Widget-Größe',
  theme: 'Menü-Design',
  lightMode: 'Heller Modus',
  darkMode: 'Dunkler Modus',
  profiles: 'Barrierefreiheitsprofile',
  contentAdjustments: 'Inhaltsanpassungen',
  colorAdjustments: 'Farbanpassungen',
  resetAll: 'Alle Einstellungen zurücksetzen',
  close: 'Barrierefreiheitsmenü schließen',
  language: 'Sprache',
  seizureSafe: 'Anfallssicher',
  visionImpaired: 'Sehbehinderung',
  adhdFriendly: 'ADHS-freundlich',
  cognitiveDisability: 'Kognitive Behinderung',
  keyboardNavigation: 'Tastaturnavigation',
  screenReader: 'Screenreader',
  colorBlind: 'Farbenblindheit',
  dyslexia: 'Legasthenie',
  contentScale: 'Inhaltsgröße',
  readableFont: 'Lesbare Schrift',
  highlightTitles: 'Überschriften hervorheben',
  fontSize: 'Schriftgröße',
  textMagnifier: 'Textlupe',
  readingLens: 'Leselupe',
  highlightLinks: 'Links hervorheben',
  lineHeight: 'Zeilenhöhe',
  letterSpacing: 'Zeichenabstand',
  textAlign: 'Textausrichtung',
  darkContrast: 'Dunkler Kontrast',
  lightContrast: 'Heller Kontrast',
  highContrast: 'Hoher Kontrast',
  monochrome: 'Monochrom',
  invertColors: 'Farben invertieren',
  highSaturation: 'Hohe Sättigung',
  lowSaturation: 'Geringe Sättigung',
  adjustTextColors: 'Textfarben anpassen',
  adjustTitleColors: 'Titelfarben anpassen',
  adjustBackgroundColors: 'Hintergrundfarben anpassen',
  cancel: 'Abbrechen',
  orientationAdjustments: 'Ausrichtungsanpassungen',
  readingGuide: 'Lesehilfe',
  stopAnimations: 'Animationen Anhalten',
  readingMask: 'Lesemaske',
  highlightHover: 'Hervorhebung beim Daraufzeigen',
  bigBlackCursor: 'Großer schwarzer Zeiger',
  bigWhiteCursor: 'Großer weißer Zeiger',
  default: 'Standard',
  decrease: 'Verringern',
  increase: 'Erhöhen',
  alignLeft: 'Linksbündig',
  alignCenter: 'Zentriert',
  alignRight: 'Rechtsbündig',
  pageAnalysis: 'Seitenanalyse',
  analyzeNow: 'Seite analysieren',
  analyzing: 'Analysiere…',
  score: 'Punktzahl',
  issuesFound: 'Probleme gefunden',
  noIssues: 'Keine Probleme gefunden',
  wcagPass: 'Bestanden',
  wcagFail: 'Fehler',
  wcagWarn: 'Warnung',
  statement: 'Erklärung',
  statementTitle: 'Erklärung zur Barrierefreiheit',
  statementDesc: 'WCAG 2.2 AA Konformität & Richtlinien',
  complianceStatus: 'Konformitätsstatus',
  statementP1: 'Wir sind fest davon überzeugt, dass das Internet für jeden verfügbar und zugänglich sein sollte, und setzen uns dafür ein, eine Website bereitzustellen, die unabhängig von den Fähigkeiten des Nutzers einem möglichst breiten Publikum zugänglich ist.',
  statementP2: 'Um dies zu erreichen, orientieren wir uns so streng wie möglich an den Web Content Accessibility Guidelines 2.2 (WCAG 2.2) des World Wide Web Consortiums (W3C) auf Stufe AA. Diese Richtlinien erklären, wie Webinhalte für Menschen mit den unterschiedlichsten Behinderungen zugänglich gemacht werden können.',
  statementP3: 'Diese Website verwendet eine Barrierefreiheits-Schnittstelle, die es Personen mit spezifischen Behinderungen ermöglicht, die Benutzeroberfläche der Website an ihre persönlichen Bedürfnisse anzupassen.',
  closeStatement: 'Erklärung schließen',
}

const pt: Translations = {
  title: 'Tech unicorn',
  subtitle: 'Configurações de Acessibilidade',
  widgetSize: 'Tamanho do Widget',
  theme: 'Tema do Menu',
  lightMode: 'Modo Claro',
  darkMode: 'Modo Escuro',
  profiles: 'Perfis de Acessibilidade',
  contentAdjustments: 'Ajustes de Conteúdo',
  colorAdjustments: 'Ajustes de Cor',
  resetAll: 'Redefinir todas as configurações',
  close: 'Fechar menu de acessibilidade',
  language: 'Idioma',
  seizureSafe: 'Seguro para Convulsões',
  visionImpaired: 'Deficiência Visual',
  adhdFriendly: 'Amigável para TDAH',
  cognitiveDisability: 'Deficiência Cognitiva',
  keyboardNavigation: 'Navegação por Teclado',
  screenReader: 'Leitor de Tela',
  colorBlind: 'Daltonismo',
  dyslexia: 'Dislexia',
  contentScale: 'Escala de Conteúdo',
  readableFont: 'Fonte Legível',
  highlightTitles: 'Destacar Títulos',
  fontSize: 'Tamanho da Fonte',
  textMagnifier: 'Lupa de Texto',
  readingLens: 'Lupa de Leitura',
  highlightLinks: 'Destacar Links',
  lineHeight: 'Altura da Linha',
  letterSpacing: 'Espaçamento de Letras',
  textAlign: 'Alinhamento de Texto',
  darkContrast: 'Contraste Escuro',
  lightContrast: 'Contraste Claro',
  highContrast: 'Alto Contraste',
  monochrome: 'Monocromático',
  invertColors: 'Inverter Cores',
  highSaturation: 'Saturação Alta',
  lowSaturation: 'Saturação Baixa',
  adjustTextColors: 'Ajustar Cores do Texto',
  adjustTitleColors: 'Ajustar Cores do Título',
  adjustBackgroundColors: 'Ajustar Cores de Fundo',
  cancel: 'Cancelar',
  orientationAdjustments: 'Ajustes de Orientação',
  readingGuide: 'Guia de Leitura',
  stopAnimations: 'Parar Animações',
  readingMask: 'Máscara de Leitura',
  highlightHover: 'Destacar ao Passar',
  bigBlackCursor: 'Cursor Preto Grande',
  bigWhiteCursor: 'Cursor Branco Grande',
  default: 'Padrão',
  decrease: 'Diminuir',
  increase: 'Aumentar',
  alignLeft: 'Alinhar à esquerda',
  alignCenter: 'Centralizar',
  alignRight: 'Alinhar à direita',
  pageAnalysis: 'Análise de Página',
  analyzeNow: 'Analisar Página',
  analyzing: 'Analisando…',
  score: 'Pontuação',
  issuesFound: 'problemas encontrados',
  noIssues: 'Nenhum problema encontrado',
  wcagPass: 'Aprovado',
  wcagFail: 'Falha',
  wcagWarn: 'Aviso',
  statement: 'Declaração',
  statementTitle: 'Declaração de Acessibilidade',
  statementDesc: 'Conformidade com WCAG 2.2 AA e políticas',
  complianceStatus: 'Status de conformidade',
  statementP1: 'Acreditamos firmemente que a internet deve estar disponível e acessível para todos e temos o compromisso de fornecer um site acessível ao público mais amplo possível, independentemente da capacidade.',
  statementP2: 'Para cumprir isso, pretendemos aderir o mais estritamente possível às Diretrizes de Acessibilidade de Conteúdo da Web 2.2 (WCAG 2.2) do World Wide Web Consortium (W3C) no nível AA. Estas diretrizes explicam como tornar o conteúdo da web acessível a pessoas com uma ampla gama de deficiências.',
  statementP3: 'Este site utiliza uma interface de acessibilidade que permite que pessoas com deficiências específicas ajustem a interface do usuário do site e a projetem de acordo com suas necessidades pessoais.',
  closeStatement: 'Fechar declaração',
}

const ar: Translations = {
  title: 'Tech unicorn',
  subtitle: 'إعدادات إمكانية الوصول',
  widgetSize: 'حجم الأداة',
  theme: 'مظهر القائمة',
  lightMode: 'الوضع الفاتح',
  darkMode: 'الوضع الداكن',
  profiles: 'ملفات إمكانية الوصول',
  contentAdjustments: 'تعديلات المحتوى',
  colorAdjustments: 'تعديلات الألوان',
  resetAll: 'إعادة تعيين جميع الإعدادات',
  close: 'إغلاق قائمة إمكانية الوصول',
  language: 'اللغة',
  seizureSafe: 'آمن للصرع',
  visionImpaired: 'ضعف البصر',
  adhdFriendly: 'مناسب لـ ADHD',
  cognitiveDisability: 'إعاقة معرفية',
  keyboardNavigation: 'التنقل بلوحة المفاتيح',
  screenReader: 'قارئ الشاشة',
  colorBlind: 'عمى الألوان',
  dyslexia: 'عسر القراءة',
  contentScale: 'مقياس المحتوى',
  readableFont: 'خط مقروء',
  highlightTitles: 'تمييز العناوين',
  fontSize: 'حجم الخط',
  textMagnifier: 'مكبر النص',
  readingLens: 'عدسة القراءة',
  highlightLinks: 'تمييز الروابط',
  lineHeight: 'ارتفاع السطر',
  letterSpacing: 'تباعد الحروف',
  textAlign: 'محاذاة النص',
  darkContrast: 'تباين داكن',
  lightContrast: 'تباين فاتح',
  highContrast: 'تباين عالٍ',
  monochrome: 'أحادي اللون',
  invertColors: 'عكس الألوان',
  highSaturation: 'تشبع عالي',
  lowSaturation: 'تشبع منخفض',
  adjustTextColors: 'تعديل ألوان النص',
  adjustTitleColors: 'تعديل ألوان العناوين',
  adjustBackgroundColors: 'تعديل ألوان الخلفية',
  cancel: 'إلغاء',
  orientationAdjustments: 'تعديلات التوجيه',
  readingGuide: 'دليل القراءة',
  stopAnimations: 'إيقاف الرسوم المتحركة',
  readingMask: 'قناع القراءة',
  highlightHover: 'تمييز عند التمرير',
  bigBlackCursor: 'مؤشر أسود كبير',
  bigWhiteCursor: 'مؤشر أبيض كبير',
  default: 'افتراضي',
  decrease: 'تقليل',
  increase: 'زيادة',
  alignLeft: 'محاذاة يسار',
  alignCenter: 'توسيط',
  alignRight: 'محاذاة يمين',
  pageAnalysis: 'تحليل الصفحة',
  analyzeNow: 'تحليل الصفحة',
  analyzing: 'جارٍ التحليل…',
  score: 'النتيجة',
  issuesFound: 'مشاكل موجودة',
  noIssues: 'لا توجد مشاكل',
  wcagPass: 'نجاح',
  wcagFail: 'فشل',
  wcagWarn: 'تحذير',
  statement: 'بيان إمكانية الوصول',
  statementTitle: 'بيان إمكانية الوصول',
  statementDesc: 'الامتثال لمعايير WCAG 2.2 AA والسياسات',
  complianceStatus: 'حالة الامتثال',
  statementP1: 'نحن نؤمن إيماناً راسخاً بأن الإنترنت يجب أن يكون متاحاً وفي متناول الجميع، ونحن ملتزمون بتوفير موقع ويب يمكن الوصول إليه لأوسع جمهور ممكن، بغض النظر عن القدرة.',
  statementP2: 'لتحقيق ذلك، نهدف إلى الالتزام بأقصى قدر ممكن بإرشادات إمكانية الوصول إلى محتوى الويب 2.2 (WCAG 2.2) الصادرة عن رابطة الشبكة العالمية (W3C) على المستوى AA. تشرح هذه الإرشادات كيفية جعل محتوى الويب متاحاً للأشخاص الذين يعانون من مجموعة واسعة من الإعاقات.',
  statementP3: 'يستخدم هذا الموقع واجهة إمكانية الوصول التي تتيح للأشخاص ذوي الإعاقات المحددة ضبط واجهة المستخدم للموقع وتصميمها وفقاً لاحتياجاتهم الشخصية.',
  closeStatement: 'إغلاق البيان',
}

const TRANSLATIONS: Record<Lang, Translations> = { en, es, fr, de, pt, ar }

export function getTranslations(lang: Lang = 'en'): Translations {
  return TRANSLATIONS[lang] ?? en
}
